import React, { useEffect, useState } from 'react';
import { isUndefined } from 'lodash';
import { DrawerBody, DrawerFooter } from '@fiverr-private/overlay';
import { withQueryClient } from '@fiverr-private/query_client_hoc';
import { calculateBriefDisplayStatus, BriefDisplayStatus } from '@fiverr-private/dynamic_matching';
import { ErrorBoundary, renderChildren } from '@fiverr-private/ui_utils';
import { logger } from '@fiverr-private/obs';
import { buyerBriefPreviewDrawerIsShown } from '../../monitoring/bqEvents';
import { LoadingState } from '../Shared/LoadingState';
import { ErrorState } from '../Shared/ErrorState';
import { useFetchBuyerBrief } from '../hooks/useFetchBuyerBrief';
import { BriefDrawer } from '../BriefDrawer';
import type { SellerBriefResponse } from '../../types/SellerBriefResponse';
import { Controls } from './Controls';
import { BuyerBriefContent } from './BuyerBriefContent';

export interface BuyerBriefDrawerProps {
  briefId: SellerBriefResponse['id'];
  source?: string;
  hideFooter?: boolean;
  children: React.ReactNode;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const onBuyerBriefDrawerRenderError = (originalError: Error, pageName?: string) => {
  const err = new Error('Buyer brief overview drawer failed to render');
  logger.error(err, {
    cause: originalError,
    pageName,
  });
};

const BuyerBriefDrawerBody: React.FC<{
  brief: SellerBriefResponse;
  hideFooter?: boolean;
  pageName?: string;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}> = ({ brief, hideFooter, pageName = '', onEdit, onDuplicate, onDelete }) => {
  const { totalCount: offersCount, acceptedCount: hiredCount } = brief.customOffersMetrics || {};
  const hasPreviousInvites = !!offersCount;
  const briefDisplayStatus = calculateBriefDisplayStatus(
    brief?.tier,
    brief?.status,
    hasPreviousInvites,
    hiredCount,
    offersCount
  );
  const noActionsStatuses = [BriefDisplayStatus.INACTIVE, BriefDisplayStatus.REJECTED];

  useEffect(() => {
    buyerBriefPreviewDrawerIsShown({ pageName, briefId: brief.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DrawerBody>
        <BuyerBriefContent brief={brief} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
      </DrawerBody>
      {!hideFooter && !noActionsStatuses.includes(briefDisplayStatus) && (
        <DrawerFooter>
          <Controls
            briefId={brief.id}
            status={brief.status}
            briefDisplayStatus={briefDisplayStatus}
            hasPreviousInvites={hasPreviousInvites}
          />
        </DrawerFooter>
      )}
    </>
  );
};

const BuyerBriefDrawer: React.FC<BuyerBriefDrawerProps> = ({
  briefId,
  source,
  hideFooter,
  children,
  onEdit,
  onDuplicate,
  onDelete,
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
}) => {
  const [opened, setOpened] = useState(false);

  const isControlled = !isUndefined(controlledIsOpen) && !!controlledOnClose;
  const isOpen = isControlled ? controlledIsOpen : opened;
  const handleClose = isControlled ? controlledOnClose : () => setOpened(false);

  const trigger = isControlled
    ? children
    : React.Children.map(children, (child) => renderChildren(child, { onClick: () => setOpened(true) }));

  const { data, isLoading, isError } = useFetchBuyerBrief({ briefId, enabled: isOpen });

  return (
    <ErrorBoundary onError={(err) => onBuyerBriefDrawerRenderError(err, source)}>
      <BriefDrawer trigger={trigger} isOpen={isOpen} onClose={handleClose}>
        {isLoading && <LoadingState />}
        {!isLoading && (isError || !data) && <ErrorState />}
        {!isLoading && !isError && data && (
          <BuyerBriefDrawerBody
            brief={data}
            hideFooter={hideFooter}
            pageName={source}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        )}
      </BriefDrawer>
    </ErrorBoundary>
  );
};

const BuyerBriefDrawerWithQueryClient = withQueryClient(BuyerBriefDrawer);

export { BuyerBriefDrawerWithQueryClient as BuyerBriefDrawer };
